<script setup>
import {
    BodyRegions,
    BodyRegionsLabels,
    Diagnosis,
    PredefinedDiagnosisList
} from "@/components/hpo/patientModal/diagnosis/parser";
import {pgettext} from "@/utils/translate";
import {mdiCheckboxBlankOutline, mdiCheckboxOutline, mdiChevronLeft} from "@mdi/js";
import {computed, ref, watch} from "vue";

const props = defineProps({
    diagnosis: String,
    predefinedDiagnosis: Array,
});

const predefinedDiagnosisList = new PredefinedDiagnosisList(props.predefinedDiagnosis);

const regionProps = {
    [BodyRegions.HEAD]: {"rounded": "pill"},
};

const diagnosisObj = ref(new Diagnosis(predefinedDiagnosisList, props.diagnosis));
watch(() => props.diagnosis, (newDiagnosisText) => {
    diagnosisObj.value = new Diagnosis(predefinedDiagnosisList, newDiagnosisText);
    selectedRegion.value = null;
});

defineExpose({
    diagnosisObj,
});

function regionNotEmpty(region) {
    return !diagnosisObj.value.regions[region].isEmpty;
}

const selectedRegion = ref(null);

function selectRegion(region) {
    selectedRegion.value = region;
}

function cancelRegionSelection() {
    selectedRegion.value = null;
}

const selectedRegionAvailableDiagnoses = computed(() => {
    if (selectedRegion.value === null) {
        return null;
    }
    return predefinedDiagnosisList.getForRegion(selectedRegion.value);
});

const selectedRegionDiagnosis = computed(() => {
    if (selectedRegion.value === null) {
        return null;
    }
    return diagnosisObj.value.regions[selectedRegion.value];
});

const notes = ref(diagnosisObj.value.notesString);
watch(diagnosisObj, (newDiagnosis) => {
    notes.value = newDiagnosis.notesString;
});

function commitNotes() {
    diagnosisObj.value.notesString = notes.value;
}

const selectedRegionNotes = ref(null);
watch(selectedRegionDiagnosis, (newDiagnosis) => {
    if (newDiagnosis === null) {
        selectedRegionNotes.value = null;
        return;
    }
    selectedRegionNotes.value = newDiagnosis.additionalNotesString;
});

function commitSelectedRegionNotes() {
    if (selectedRegionNotes.value === null || selectedRegionDiagnosis.value === null) {
        return;
    }
    selectedRegionDiagnosis.value.additionalNotesString = selectedRegionNotes.value;
}
</script>

<template>
    <div class="diagnosis-builder">
        <div class="diagnosis-builder__dummy" :class="{'diagnosis-builder__dummy--selected': selectedRegion === null}">
            <div class="diagnosis-builder__body-region-grid">
                <v-btn v-for="(label, region) in BodyRegionsLabels" :key="region"
                       :class="[`diagnosis-builder__body-region--${region}`]"
                       :color="regionNotEmpty(region) ? 'red-darken-3' : 'blue-darken-2'"
                       :variant="selectedRegion === region ? 'flat' : 'tonal'"
                       height="auto"
                       rounded="sm"
                       v-bind="regionProps[region]"
                       @click="selectRegion(region)">
                    {{ label }}
                </v-btn>
            </div>

            <p class="mt-4">
                <v-text-field v-model="notes"
                              :label="pgettext('label', 'Additional records')"
                              @change="commitNotes()"/>
            </p>
        </div>
        <div class="diagnosis-builder__detail"
             :class="{'diagnosis-builder__detail--selected': selectedRegion !== null}">
            <template v-if="selectedRegion !== null">
                <div class="d-md-none mb-6">
                    <v-btn :prepend-icon="mdiChevronLeft"
                           color="grey"
                           @click="cancelRegionSelection">
                        {{ gettext('Back') }}
                    </v-btn>
                </div>

                <div class="diagnosis-builder__diagnosis-types">
                    <h3>{{ BodyRegionsLabels[selectedRegion] }}</h3>

                    <v-btn v-for="type in selectedRegionAvailableDiagnoses" :key="type"
                           :variant="selectedRegionDiagnosis.hasDiagnosis(type) ? 'flat' : 'tonal'"
                           :prepend-icon="selectedRegionDiagnosis.hasDiagnosis(type) ? mdiCheckboxOutline : mdiCheckboxBlankOutline"
                           size="large"
                           @click="selectedRegionDiagnosis.toggleDiagnosis(type)">
                        {{ type }}
                    </v-btn>
                    <v-text-field v-model="selectedRegionNotes"
                                  :label="pgettext('label', 'Additional records')"
                                  @change="commitSelectedRegionNotes()"/>
                </div>
            </template>
            <v-empty-state v-else
                           :title="pgettext('label', 'Select a body region on the left first.')"/>
        </div>
    </div>
</template>
